import { age } from "./body";
import { older, type DeviceAnswers } from "./eating";
import { familyByKey, inFamily, type FoodFamily } from "./foodTags";
import { dailyTargets } from "./nutrition";
import type {
  AvoidKey,
  EatingAnswers,
  EatingCondition,
  EatingMed,
  EatingPatternAnswer,
  EatingProfile,
  EatingWindowAnswer,
  KayaState,
  MealsAnswer,
  TriggerKey,
} from "./types";

/**
 * "Plan my food": which questions a person is asked, how they are numbered, what each option says
 * it changes, and the answers read back as sentences. (ADR-042; plan §3.4; the Eating* boards)
 *
 * Numbering (EXAMPLE-DATA "Eating answers"): the progress line counts only the screens this
 * person will see. The low-sugar follow-up opens inline on the conditions screen but counts as its
 * own step; the follow-ups under "2 — morning and evening", an eating window and "A weekday" do
 * not — they carry their question's number. The optional safety question is never counted ("Last
 * question · optional"). So the main person sees 13 and the parent at 65 with prediabetes 15.
 */

export type StepId =
  | "pregnant"
  | "pattern"
  | "avoid"
  | "dislikes"
  | "conditions"
  | "hypo"
  | "meds"
  | "chewing"
  | "meals"
  | "window"
  | "cook"
  | "lunch"
  | "fasts"
  | "triggers"
  | "cravings"
  | "sleep";

/** A screen is a step, except the low-sugar follow-up, which lives on the conditions screen. */
export type ScreenId = Exclude<StepId, "hypo"> | "safety";

/** Pregnancy is asked of women of 18–50, and of a woman whose age Kaya does not know. */
export const PREGNANCY_AGES = { from: 18, to: 50 } as const;

/** The later answers some steps branch on, taken from the draft being answered. */
function ctx(S: KayaState, a: EatingAnswers, today: Date) {
  const years = age(S, today);
  return {
    female: S.profile.sex === "female",
    years,
    older: older(S, today),
    anyCondition: (a.conditions ?? []).length > 0,
    diabetes: (a.conditions ?? []).includes("diabetes"),
  };
}

/** The counted steps this person will see, in order. */
export function eatingSteps(S: KayaState, a: EatingAnswers, today = new Date()): StepId[] {
  const c = ctx(S, a, today);
  const out: StepId[] = [];
  if (c.female && (c.years === null || (c.years >= PREGNANCY_AGES.from && c.years <= PREGNANCY_AGES.to)))
    out.push("pregnant");
  out.push("pattern", "avoid", "dislikes", "conditions");
  if (c.diabetes) out.push("hypo");
  if (c.older || c.anyCondition) out.push("meds");
  if (c.older) out.push("chewing");
  out.push("meals", "window", "cook", "lunch", "fasts", "triggers", "cravings", "sleep");
  return out;
}

/** The screens in order, the safety question last. */
export function eatingScreens(S: KayaState, a: EatingAnswers, today = new Date()): ScreenId[] {
  return [...eatingSteps(S, a, today).filter((s): s is Exclude<StepId, "hypo"> => s !== "hypo"), "safety"];
}

/** "Question 4 of 15", as numbers; null for the safety question, which is not counted. */
export function stepCount(
  S: KayaState,
  a: EatingAnswers,
  id: StepId | "safety",
  today = new Date()
): { n: number; of: number } | null {
  if (id === "safety") return null;
  const steps = eatingSteps(S, a, today);
  const i = steps.indexOf(id);
  return i < 0 ? null : { n: i + 1, of: steps.length };
}

/**
 * The answers as they will be saved: an answer to a question this person is no longer asked goes
 * back to "not asked" — a pregnancy answer after the birthday that takes her past 50, the
 * low-sugar answer once diabetes is unticked, the medicines once no condition is left under 60 —
 * so no rule reads an answer the screens no longer show. Follow-ups that no longer apply go too.
 */
export function pruneAnswers(S: KayaState, a: EatingAnswers, today = new Date()): EatingAnswers {
  const steps = new Set(eatingSteps(S, a, today));
  const out: EatingAnswers = { ...a };
  if (!steps.has("pregnant")) delete out.pregnant;
  if (!steps.has("hypo")) {
    delete out.hypoAnswer;
    delete out.hypoRisk;
  }
  if (!steps.has("meds")) delete out.meds;
  if (!steps.has("chewing")) delete out.chewing;
  if (out.meals !== "2") delete out.evening;
  if (out.window !== "16:8" && out.window !== "14:10") delete out.windowStart;
  if (!(out.triggers ?? []).length) delete out.triggerMode;
  return out;
}

/** The share of the bar, to one decimal place as the boards draw it (2 of 13 → 15.4). */
export function progressPct(n: number, of: number): number {
  return of > 0 ? Math.round((n / of) * 1000) / 10 : 0;
}

/** At 60+ or on a drug that can bring sugar too low, Kaya plans three meals and a snack, and a
 *  two-meal day, one main meal or an eating window is explained rather than applied. */
export function mealsRestricted(S: KayaState, a: EatingAnswers, today = new Date()): "60+" | "hypo" | null {
  if (older(S, today)) return "60+";
  return a.hypoRisk === true ? "hypo" : null;
}

/* ---------- the options, and what each one changes ---------- */

export interface Opt<T extends string> {
  value: T;
  label: string;
  /** what the answer changes, when it changes something worth saying */
  sub?: string;
}

export const PREGNANT_OPTS: Opt<NonNullable<EatingProfile["pregnant"]>>[] = [
  { value: "no", label: "No", sub: "Targets as they are" },
  { value: "pregnant", label: "Pregnant", sub: "No deficit; a doctor should set your targets" },
  { value: "breastfeeding", label: "Breastfeeding", sub: "No deficit; a doctor should set your targets" },
];

export const PATTERN_OPTS: Opt<EatingPatternAnswer>[] = [
  { value: "veg", label: "Vegetarian", sub: "No meat, fish or egg; milk and curd are in" },
  { value: "egg", label: "Egg + veg", sub: "Vegetarian, plus eggs" },
  { value: "nonveg", label: "Non-veg", sub: "Meat, fish and egg are all in" },
  { value: "vegan", label: "Vegan", sub: "Also no milk, curd, ghee, paneer or honey" },
  { value: "jain", label: "Jain", sub: "Also no root vegetables or honey; dinner before sunset" },
  { value: "satvik", label: "No onion-garlic (Satvik)", sub: "Only dishes made without them" },
];

export const AVOID_OPTS: Opt<AvoidKey>[] = [
  { value: "peanut", label: "Peanut" },
  { value: "treenut", label: "Tree nuts", sub: "Cashew, almond, walnut, pista" },
  { value: "milk", label: "Milk", sub: "Allergy: no milk, curd, paneer or ghee" },
  { value: "lactose", label: "Lactose", sub: "Small amounts OK — curd and paneer stay, no glasses of milk" },
  { value: "wheat", label: "Wheat / gluten" },
  { value: "egg", label: "Egg" },
  { value: "soy", label: "Soy" },
  { value: "sesame", label: "Sesame" },
  { value: "fish", label: "Fish / shellfish" },
];

/** The chips of the dislikes screen, each a dish family (`foodTags.ts::FOOD_FAMILIES`). */
export const DISLIKE_CHIPS: readonly string[] = [
  "karela", "baingan", "lauki", "tinda", "mushroom", "paneer", "rajma", "chana", "oats", "curd",
  "sprouts", "soya", "fish", "egg", "chicken", "methi", "palak", "raita", "upma", "dalia",
];

export const CONDITION_OPTS: Opt<EatingCondition>[] = [
  { value: "diabetes", label: "Diabetes", sub: "Carbohydrate spread across the day; deficit 500 at most" },
  { value: "prediabetes", label: "Prediabetes", sub: "Told your sugar is borderline: walks after meals help most" },
  { value: "bp", label: "High BP", sub: "Salt ceiling up front; salty dishes ranked down" },
  { value: "cholesterol", label: "High cholesterol", sub: "Saturated-fat swaps first" },
  { value: "thyroid", label: "Thyroid" },
  { value: "pcos", label: "PCOS" },
  { value: "anaemia", label: "Anaemia", sub: "Iron-rich picks; tea kept away from meals" },
  { value: "liver", label: "Fatty liver" },
  { value: "kidney", label: "Kidney disease", sub: "Protein not raised — your doctor sets it" },
  // Asked for the exercise plan (ADR-053), kept on this one list so each is answered once.
  { value: "heart", label: "A heart condition", sub: "Exercise plan only" },
  { value: "osteoporosis", label: "Osteoporosis", sub: "Exercise plan only: no loaded bending or twisting" },
  { value: "lung", label: "Asthma or COPD", sub: "Exercise plan only" },
  { value: "stroke", label: "A past stroke", sub: "Exercise plan only" },
  { value: "jointReplacement", label: "A joint replacement", sub: "Exercise plan only" },
];

export const HYPO_OPTS: Opt<NonNullable<EatingProfile["hypoAnswer"]>>[] = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "unsure", label: "Not sure" },
];

export const MED_OPTS: Opt<EatingMed>[] = [
  { value: "thyroid", label: "Thyroid tablet" },
  { value: "warfarin", label: "Blood thinner (warfarin)" },
  { value: "metformin", label: "Metformin" },
  { value: "bp", label: "BP tablets" },
  {
    value: "glp1",
    label: "A GLP-1 or weight-loss medicine",
    sub: "Protein floor 1.2 g/kg, smaller meals, the deficit never raised; your doctor sets the dose",
  },
];

/** The notes a medicine adds, in the board's order. `off` is the muted line when not ticked. */
export function medNotes(proteinG: number | null): { med: EatingMed; title: string; on?: string; off: string }[] {
  return [
    {
      med: "thyroid",
      title: "Breakfast 30–60 min after the thyroid tablet",
      on: "Calcium and iron kept apart from it",
      off: "Only with a thyroid tablet — not ticked",
    },
    { med: "warfarin", title: "Leafy greens kept steady, not swung", off: "Only on a blood thinner — not ticked" },
    {
      med: "glp1",
      title: `Protein at least ${proteinG === null ? "1.2 g per kg" : `${proteinG} g`}, in smaller meals; the deficit never raised`,
      on: "Kaya never touches the dose.",
      off: "Only on a GLP-1 medicine — not ticked. Kaya never touches the dose.",
    },
    { med: "metformin", title: "Ask your doctor about B12 on metformin", off: "Only on metformin — not ticked" },
    {
      med: "bp",
      title: "Never a potassium salt substitute",
      on: "It does not mix with BP tablets",
      off: "Only on BP tablets — not ticked",
    },
  ];
}

export const CHEWING_OPTS: Opt<NonNullable<EatingProfile["chewing"]>>[] = [
  { value: "no", label: "No", sub: "Any texture" },
  { value: "some", label: "Some", sub: "Softer dishes first — dal, khichdi, curd, idli, upma" },
  { value: "yes", label: "Yes", sub: "Only soft dishes; hard and dry ones left out" },
];

export const MEALS_OPTS: Opt<MealsAnswer>[] = [
  { value: "2", label: "2 — morning and evening", sub: "One big meal each end of the day" },
  { value: "3", label: "3", sub: "Breakfast, lunch, dinner" },
  { value: "3s", label: "3 + snack", sub: "Plus one in the evening" },
  { value: "45", label: "4–5 small" },
  { value: "1", label: "1 main meal", sub: "At 60+, or on low-sugar tablets, Kaya explains why it plans more." },
];

export const EVENING_OPTS: Opt<NonNullable<EatingProfile["evening"]>>[] = [
  { value: "full", label: "Full meal" },
  { value: "light", label: "Light" },
  { value: "varies", label: "Varies" },
];

export const WINDOW_OPTS: Opt<EatingWindowAnswer>[] = [
  { value: "none", label: "No — any time", sub: "Meals where they fall today" },
  { value: "16:8", label: "16:8 — eat within 8 hours", sub: "e.g. 12 to 8; no breakfast slot, the first meal takes its share" },
  { value: "14:10", label: "14:10 — within 10 hours", sub: "e.g. 10 to 8" },
  { value: "sunset", label: "Not after sunset", sub: "Dinner planned before it; Jain already sets this" },
];

/** Where each window usually opens (hours), and its length. */
export const WINDOW_STARTS: Record<"16:8" | "14:10", { hours: number; starts: number[]; usual: number }> = {
  "16:8": { hours: 8, starts: [10, 12, 14], usual: 12 },
  "14:10": { hours: 10, starts: [8, 10, 12], usual: 10 },
};

export const COOK_OPTS: Opt<NonNullable<EatingProfile["cook"]>>[] = [
  { value: "quick", label: "I do, 15 minutes or less", sub: "One-pot and no-cook dishes first" },
  { value: "30", label: "I do, about 30 minutes", sub: "Everyday home dishes" },
  { value: "long", label: "I do, and take my time", sub: "Slower dishes too" },
  { value: "family", label: "Family or a cook", sub: "Portions of what is cooked at home; never a dish nobody makes" },
];

export const LUNCH_OPTS: Opt<NonNullable<EatingProfile["lunch"]>>[] = [
  { value: "home", label: "At home", sub: "Planned like any meal" },
  { value: "tiffin", label: "Tiffin from home", sub: "Dishes that travel — roti-sabzi, pulao, parantha with curd" },
  { value: "out", label: "Canteen or outside", sub: "Left as a budget — kcal and protein, not dishes" },
];

export const FAST_OPTS: Opt<string>[] = [
  { value: "weekday", label: "A weekday", sub: "Pick the day" },
  { value: "ekadashi", label: "Ekadashi", sub: "Twice a month" },
  { value: "navratri", label: "Navratri" },
  { value: "ramadan", label: "Ramadan" },
  { value: "other", label: "Other days", sub: "Mark them on the day" },
];

export const WEEKDAYS: Opt<string>[] = [
  { value: "mon", label: "Mon" },
  { value: "tue", label: "Tue" },
  { value: "wed", label: "Wed" },
  { value: "thu", label: "Thu" },
  { value: "fri", label: "Fri" },
  { value: "sat", label: "Sat" },
  { value: "sun", label: "Sun" },
];

export const TRIGGER_OPTS: Opt<TriggerKey>[] = [
  { value: "sweets", label: "Sweets / mithai" },
  { value: "fried", label: "Fried snacks" },
  { value: "chips", label: "Chips" },
  { value: "bakery", label: "Bakery / biscuits" },
  { value: "colddrinks", label: "Cold drinks" },
  { value: "chocolate", label: "Chocolate / ice cream" },
];

export const TRIGGER_MODE_OPTS: Opt<NonNullable<EatingProfile["triggerMode"]>>[] = [
  { value: "2x", label: "Plan a small portion 2× a week", sub: "Say a 40 g piece of barfi, ~180 kcal, in a snack slot" },
  { value: "3x", label: "3× a week" },
  { value: "most", label: "Most days" },
  { value: "out", label: "Keep them out of my plan", sub: "Still searchable and loggable" },
  { value: "numbers", label: "Just show me the numbers" },
];

export const CRAVING_OPTS: Opt<NonNullable<EatingProfile["cravingTime"]>>[] = [
  { value: "afternoon", label: "Afternoon", sub: "A snack with protein and fibre just before it" },
  { value: "evening", label: "Evening", sub: "A snack with protein and fibre just before it" },
  { value: "night", label: "Late at night", sub: "More protein and fibre at dinner" },
  { value: "stress", label: "When stressed", sub: "One line on what to reach for first" },
  { value: "rarely", label: "Rarely", sub: "Nothing changes" },
];

export const SLEEP_OPTS: Opt<NonNullable<EatingProfile["sleep"]>>[] = [
  { value: "<6", label: "Under 6 hours", sub: "Food says so: short sleep adds about 385 kcal of appetite a day" },
  { value: "6-7", label: "6–7 hours" },
  { value: "7+", label: "7 hours or more" },
];

export const SHIFT_OPTS: Opt<"no" | "yes">[] = [
  { value: "no", label: "No" },
  { value: "yes", label: "Yes", sub: "Meals follow your waking hours, not the clock" },
];

/* ---------- dislikes ---------- */

/** A food the dislikes can name: the table's rows and the person's own foods alike. */
export interface NamedFood {
  i: string;
  n: string;
}

export interface DislikeView {
  key: string;
  label: string;
  kind: "family" | "food";
}

/**
 * The dislikes as the screen shows them, split into the ones still there and the ids whose row a
 * rebuilt table dropped — those are said once and left out of the next save. A family never goes
 * missing: it is a word, not a row.
 */
export function resolveDislikes(
  keys: readonly string[],
  lookup: (id: string) => NamedFood | null
): { shown: DislikeView[]; missing: string[] } {
  const shown: DislikeView[] = [];
  const missing: string[] = [];
  for (const key of keys) {
    const fam = familyByKey(key);
    if (fam) {
      shown.push({ key, label: fam.label, kind: "family" });
      continue;
    }
    const f = lookup(key);
    if (f) shown.push({ key, label: f.n, kind: "food" });
    else missing.push(key);
  }
  return { shown, missing };
}

/** Whether a food is one the person said they dislike — by its id, or by a family its name is in. */
export function isDisliked(f: NamedFood, keys: readonly string[]): boolean {
  for (const key of keys) {
    if (key === f.i) return true;
    const fam = familyByKey(key);
    if (fam && inFamily(f.n, fam)) return true;
  }
  return false;
}

export function familyCount(fam: FoodFamily, foods: readonly NamedFood[]): number {
  return foods.filter((f) => inFamily(f.n, fam)).length;
}

/**
 * The dislike chips a pattern already keeps out — "Fish and chicken are already out — you eat egg
 * and veg" — so they show disabled rather than as a second way to say the same thing.
 */
export function ruledOutByPattern(p: EatingPatternAnswer | undefined): string[] {
  switch (p) {
    case "veg":
    case "satvik":
      return ["fish", "egg", "chicken"];
    case "egg":
      return ["fish", "chicken"];
    case "vegan":
      return ["paneer", "curd", "fish", "egg", "chicken", "raita"];
    case "jain":
      return ["mushroom", "fish", "egg", "chicken"];
    default:
      return [];
  }
}

export const PATTERN_WORDS: Record<EatingPatternAnswer, string> = {
  veg: "you eat vegetarian",
  egg: "you eat egg and veg",
  nonveg: "you eat non-veg",
  vegan: "you eat vegan",
  jain: "you eat Jain",
  satvik: "you eat no onion or garlic",
};

export function ruledOutLine(p: EatingPatternAnswer | undefined): string | null {
  if (!p) return null;
  const out = ruledOutByPattern(p).map((k) => familyByKey(k)?.label.toLowerCase() ?? k);
  if (!out.length) return null;
  return `${cap(join(out))} ${out.length === 1 ? "is" : "are"} already out — ${PATTERN_WORDS[p]}.`;
}

/* ---------- meals a day, and the day's split ---------- */

export interface SplitRow {
  label: string;
  pct: number;
  kcal: number;
}

export interface DaySplit {
  rows: SplitRow[];
  /** kcal the plan leaves unplanned — a light evening's gap */
  unplanned: number;
}

/**
 * How a day's budget is shared between meals, shaped on ICMR-NIN's sample days (plan §3.5.1):
 * the adult 3 + snack day 25 / 35 / 10 / 30 %, the elderly day 24 / 44 / 4 / 28 %. A light
 * evening is ~350 kcal of seeds, nuts, fruit or curd and the morning carries 72 %, so that day
 * lands under budget and the screen says by how much. At 60+ or on low-sugar tablets a two-meal
 * day or one main meal is planned as three meals and a snack.
 *
 * Each share is rounded to 10 kcal and the rounding lands on the first meal, so the rows add up to
 * the budget as the boards draw them (460 + 630 + 180 + 540 = 1,810).
 */
export const LIGHT_EVENING_KCAL = 350;
export const TWO_MEAL_MORNING = 0.72;

export function daySplit(
  meals: MealsAnswer,
  evening: EatingProfile["evening"],
  opts: { older: boolean; restricted: boolean },
  budget: number
): DaySplit {
  const m = opts.restricted && (meals === "2" || meals === "1") ? "3s" : meals;
  const shares = (rows: [string, number][]): DaySplit => {
    const out = rows.map(([label, pct]) => ({ label, pct, kcal: Math.round((budget * pct) / 100 / 10) * 10 }));
    const diff = budget - out.reduce((a, r) => a + r.kcal, 0);
    if (out.length && Math.abs(diff) < 50) out[0].kcal += Math.round(diff / 10) * 10;
    return { rows: out, unplanned: 0 };
  };
  if (m === "2") {
    if (evening === "full") return shares([["Morning", 50], ["Evening", 50]]);
    const morning = Math.round((budget * TWO_MEAL_MORNING) / 10) * 10;
    const light = Math.min(LIGHT_EVENING_KCAL, Math.max(0, budget - morning));
    return {
      rows: [
        { label: "Morning", pct: Math.round((morning / budget) * 100), kcal: morning },
        { label: "Evening", pct: Math.round((light / budget) * 100), kcal: light },
      ],
      unplanned: Math.max(0, budget - morning - light),
    };
  }
  if (m === "1") return shares([["Main meal", 75], ["Something small", 25]]);
  if (m === "3") return opts.older ? shares([["Breakfast", 26], ["Lunch", 46], ["Dinner", 28]]) : shares([["Breakfast", 30], ["Lunch", 40], ["Dinner", 30]]);
  if (m === "45")
    return shares([
      ["Breakfast", 20],
      ["Mid-morning", 10],
      ["Lunch", 30],
      ["Evening", 10],
      ["Dinner", 30],
    ]);
  return opts.older
    ? shares([["Breakfast", 24], ["Lunch", 44], ["Evening", 4], ["Dinner", 28]])
    : shares([["Breakfast", 25], ["Lunch", 35], ["Evening", 10], ["Dinner", 30]]);
}

/* ---------- the eating window ---------- */

/** "12", "4", "7:30" — the boards' clock words, no am/pm inside a sentence that already says it. */
export function clockWord(h: number): string {
  const hh = Math.floor(h) % 24;
  const mm = Math.round((h - Math.floor(h)) * 60);
  const twelve = hh % 12 === 0 ? 12 : hh % 12;
  return mm ? `${twelve}:${String(mm).padStart(2, "0")}` : String(twelve);
}

/** "10 am", "12 pm", "2 pm" — the start's own label. */
export function startLabel(h: number): string {
  return `${clockWord(h)} ${h < 12 ? "am" : "pm"}`;
}

/** When the meals of a windowed day fall: the first at the start, the last half an hour before
 *  the window shuts, the rest spread between. A window moves the slots, never the budget. */
export function windowTimes(meals: MealsAnswer, start: number, hours: number): number[] {
  const last = start + hours - 0.5;
  switch (meals) {
    case "1":
      return [start + hours / 2];
    case "2":
      return [start, last];
    case "45":
      return [start, start + (last - start) / 3, start + (2 * (last - start)) / 3, last];
    default:
      return [start, start + hours / 2, last];
  }
}

export function windowLine(meals: MealsAnswer, w: "16:8" | "14:10", start: number): string {
  const t = windowTimes(meals, start, WINDOW_STARTS[w].hours).map(clockWord);
  return `Meals at about ${join(t)} — the first one carries breakfast’s share and its protein. Nothing is planned before ${clockWord(start)}.`;
}

/* ---------- read back ---------- */

const MEAL_WORDS: Record<MealsAnswer, string> = {
  "2": "Two meals, morning and evening",
  "3": "Three meals",
  "3s": "Three meals and an evening snack",
  "45": "Four or five small meals",
  "1": "One main meal",
};

export const CONDITION_WORDS: Record<EatingCondition, string> = {
  diabetes: "diabetes",
  prediabetes: "prediabetes",
  bp: "high BP",
  cholesterol: "high cholesterol",
  thyroid: "thyroid",
  pcos: "PCOS",
  anaemia: "anaemia",
  liver: "fatty liver",
  kidney: "kidney disease",
  heart: "a heart condition",
  osteoporosis: "osteoporosis",
  lung: "asthma or COPD",
  stroke: "a past stroke",
  jointReplacement: "a joint replacement",
};

export const MED_WORDS: Record<EatingMed, string> = {
  thyroid: "a thyroid tablet",
  warfarin: "a blood thinner",
  metformin: "metformin",
  bp: "BP tablets",
  glp1: "a GLP-1 or weight-loss medicine",
};

const AVOID_WORDS: Record<AvoidKey, string> = {
  peanut: "peanut",
  treenut: "tree nuts",
  milk: "milk",
  lactose: "lactose",
  wheat: "wheat",
  egg: "egg",
  soy: "soy",
  sesame: "sesame",
  fish: "fish or shellfish",
};

const TRIGGER_WORDS: Record<TriggerKey, { many: string; one: string }> = {
  sweets: { many: "sweets", one: "sweet" },
  fried: { many: "fried snacks", one: "fried snack" },
  chips: { many: "chips", one: "packet of chips" },
  bakery: { many: "bakery and biscuits", one: "bakery treat" },
  colddrinks: { many: "cold drinks", one: "cold drink" },
  chocolate: { many: "chocolate and ice cream", one: "chocolate or ice cream" },
};

const FAST_WORDS: Record<string, string> = {
  ekadashi: "Ekadashi",
  navratri: "Navratri",
  ramadan: "Ramadan",
  other: "other days",
  mon: "Mondays",
  tue: "Tuesdays",
  wed: "Wednesdays",
  thu: "Thursdays",
  fri: "Fridays",
  sat: "Saturdays",
  sun: "Sundays",
};

const MODE_WORDS: Record<NonNullable<EatingProfile["triggerMode"]>, string> = {
  "2x": "twice a week",
  "3x": "three times a week",
  most: "on most days",
  out: "",
  numbers: "",
};

export function join(xs: readonly string[], last = "and"): string {
  if (xs.length <= 1) return xs.join("");
  return `${xs.slice(0, -1).join(", ")} ${last} ${xs[xs.length - 1]}`;
}

export function cap(s: string): string {
  return s ? s[0].toUpperCase() + s.slice(1) : s;
}

/** A dislike as it reads inside a sentence: a family in lower case, a dish by its own name. */
function dislikeWords(a: EatingAnswers, lookup: (id: string) => NamedFood | null): string[] {
  return resolveDislikes(a.dislikes ?? [], lookup).shown.map((d) =>
    d.kind === "family" ? d.label.toLowerCase() : d.label
  );
}

/**
 * The answers as four sentences, before anything is saved — the EatingReadBack board. A sentence
 * about a question the person was not asked leaves that part out rather than guessing.
 */
export function readBack(
  S: KayaState,
  a: EatingAnswers,
  lookup: (id: string) => NamedFood | null,
  today = new Date()
): string[] {
  const out: string[] = [];

  // 1. what you eat
  {
    const parts: string[] = [];
    const eat = a.pattern ? PATTERN_WORDS[a.pattern] : "";
    const never = dislikeWords(a, lookup);
    if (eat && never.length) parts.push(`${cap(eat)}, never ${join(never, "or")}.`);
    else if (eat) parts.push(`${cap(eat)}.`);
    else if (never.length) parts.push(`Never ${join(never, "or")}.`);
    const avoid = a.avoid ?? [];
    if (a.avoid !== undefined) {
      const hard = avoid.filter((k) => k !== "lactose").map((k) => AVOID_WORDS[k]);
      if (!avoid.length) parts.push("No allergies.");
      else {
        if (hard.length) parts.push(`Never ${join(hard, "or")}, as an allergy.`);
        if (avoid.includes("lactose")) parts.push("Milk only in small amounts.");
      }
    }
    if (parts.length) out.push(parts.join(" "));
  }

  // 2. health
  {
    const parts: string[] = [];
    const conds = a.conditions ?? [];
    let first = "";
    if (a.conditionsPrivate) first = "Conditions not said";
    else if (a.conditions !== undefined)
      first = conds.length ? cap(join(conds.map((c) => CONDITION_WORDS[c]))) : "No conditions";
    if (conds.includes("diabetes") && a.hypoAnswer)
      first += a.hypoAnswer === "no"
        ? "; no low-sugar tablets"
        : a.hypoAnswer === "yes"
          ? "; on insulin or low-sugar tablets"
          : "; not sure about low-sugar tablets";
    if (a.pregnant) {
      const preg =
        a.pregnant === "no" ? "not pregnant or breastfeeding" : a.pregnant === "pregnant" ? "pregnant" : "breastfeeding";
      first = first ? `${first}, ${preg}` : cap(preg);
    }
    if (first) parts.push(`${first}.`);
    if (a.meds !== undefined)
      parts.push(a.meds.length ? `You take ${join(a.meds.map((m) => MED_WORDS[m]))}.` : "No regular medicines of these.");
    if (a.chewing === "some") parts.push("Some trouble chewing hard foods.");
    else if (a.chewing === "yes") parts.push("Hard foods are difficult to chew.");
    if (parts.length) out.push(parts.join(" "));
  }

  // 3. the day
  {
    const parts: string[] = [];
    if (a.meals) {
      let meals = MEAL_WORDS[a.meals];
      if (a.meals === "2" && a.evening)
        meals += a.evening === "full" ? ", both full" : a.evening === "light" ? ", the evening light" : ", the evening varies";
      const w = a.window;
      const when =
        !w || w === "none"
          ? ", at any time of day"
          : w === "sunset"
            ? ", none after sunset"
            : `, inside ${WINDOW_STARTS[w].hours} hours from ${startLabel(a.windowStart ?? WINDOW_STARTS[w].usual)}`;
      const restricted = mealsRestricted(S, a, today);
      const note =
        restricted && (a.meals === "2" || a.meals === "1" || (w && w !== "none" && w !== "sunset"))
          ? " — Kaya plans three meals and a snack instead"
          : "";
      parts.push(`${meals}${when}${note}.`);
    }
    if (a.cook) {
      const cook =
        a.cook === "family"
          ? "Family or a cook makes your meals"
          : a.cook === "quick"
            ? "You cook, 15 minutes or less"
            : a.cook === "30"
              ? "You cook, about 30 minutes"
              : "You cook, and take your time";
      const lunch =
        a.lunch === "tiffin"
          ? ", and take a tiffin on weekdays"
          : a.lunch === "out"
            ? ", and eat lunch out on weekdays"
            : a.lunch === "home"
              ? ", and lunch is at home"
              : "";
      parts.push(`${cook}${lunch}.`);
    }
    if (a.fasts !== undefined)
      parts.push(a.fasts.length ? `You fast on ${join(a.fasts.map((f) => FAST_WORDS[f] ?? f))}.` : "No fast days.");
    if (parts.length) out.push(parts.join(" "));
  }

  // 4. habits
  {
    const parts: string[] = [];
    const t = a.triggers ?? [];
    if (a.triggers !== undefined) {
      if (!t.length) parts.push("Nothing is hard to stop.");
      else {
        const what = cap(join(t.map((k) => TRIGGER_WORDS[k].many)));
        const mode = a.triggerMode ?? "2x";
        if (mode === "out") parts.push(`${what} stay out of your plan; you can still log them.`);
        else if (mode === "numbers") parts.push(`${what}: Kaya just shows the numbers.`);
        else parts.push(`${what} come as a small planned portion ${MODE_WORDS[mode]}.`);
      }
    }
    const c = a.cravingTime;
    const crave =
      c === "afternoon"
        ? "Cravings hit in the afternoon"
        : c === "evening"
          ? "Cravings hit in the evening"
          : c === "night"
            ? "Cravings hit late at night"
            : c === "stress"
              ? "Cravings come with stress"
              : c === "rarely"
                ? "Cravings are rare"
                : "";
    const sleep =
      a.sleep === "<6" ? "you sleep under 6 hours" : a.sleep === "6-7" ? "you sleep 6–7 hours" : a.sleep === "7+" ? "you sleep 7 hours or more" : "";
    const shift = a.shift ? ", and work shifts" : "";
    if (crave && sleep) parts.push(`${crave}; ${sleep}${shift}.`);
    else if (crave) parts.push(`${crave}.`);
    else if (sleep) parts.push(`${cap(sleep)}${shift}.`);
    if (parts.length) out.push(parts.join(" "));
  }
  return out;
}

const MEALS_SHORT: Record<MealsAnswer, string> = {
  "2": "2 meals",
  "3": "3 meals",
  "3s": "3 meals + snack",
  "45": "4–5 small meals",
  "1": "1 main meal",
};

/** The saved answers as one row value — "Vegetarian · High BP, diabetes, thyroid · 3 meals +
 *  snack · Ekadashi" (YouFoodParent) — or null before the questionnaire has been saved. */
export function answersSummary(S: KayaState): string | null {
  const e = S.profile.eating;
  if (!e || e.at === undefined) return null;
  const parts: string[] = [];
  const pattern = PATTERN_OPTS.find((o) => o.value === e.pattern);
  if (pattern) parts.push(pattern.label);
  const conds = e.conditions ?? [];
  if (conds.length) {
    const words = conds.map((c) => (c === "diabetes" ? "diabetes" : CONDITION_WORDS[c]));
    parts.push(cap(words.join(", ")));
  }
  if (e.meals) parts.push(MEALS_SHORT[e.meals]);
  const fasts = (e.fasts ?? []).map((f) => FAST_WORDS[f] ?? f);
  if (fasts.length) parts.push(cap(fasts.join(", ")));
  return parts.length ? parts.join(" · ") : "Answered";
}

export interface Changes {
  targets: string;
  plan: string;
  today: string;
}

/**
 * What saving would change, in three rows: the targets (worked out both ways, so "No change" is a
 * fact, not a guess), what the plan will do with the answers, and what Today will show.
 */
export function whatChanges(
  S: KayaState,
  a: EatingAnswers,
  lookup: (id: string) => NamedFood | null,
  device: { before: DeviceAnswers; after: DeviceAnswers },
  today = new Date()
): Changes {
  const withAnswers: KayaState = { ...S, profile: { ...S.profile, eating: { ...S.profile.eating, ...a } } };
  const before = dailyTargets(S, today, undefined, device.before);
  const after = dailyTargets(withAnswers, today, undefined, device.after);
  const kcal = (v: number | null) => (v === null ? "– kcal" : `~${v.toLocaleString()} kcal est.`);
  const now = `${kcal(after.kcal)} · ${after.protein.toLocaleString()} g protein`;
  const was: string[] = [];
  if (before.kcal !== after.kcal) was.push(before.kcal === null ? "–" : `~${before.kcal.toLocaleString()}`);
  if (before.protein !== after.protein) was.push(`${before.protein} g`);
  const targets = was.length ? `${now} — was ${was.join(" · ")}` : `No change — ${now}`;

  const plan: string[] = [];
  const never = dislikeWords(a, lookup);
  if (never.length) plan.push(`No ${join(never, "or")}`);
  if (a.lunch === "tiffin") plan.push("tiffin lunches");
  else if (a.lunch === "out") plan.push("lunch left as a budget");
  const t = a.triggers ?? [];
  const mode = a.triggerMode ?? "2x";
  if (t.length && (mode === "2x" || mode === "3x" || mode === "most"))
    plan.push(`a small ${TRIGGER_WORDS[t[0]].one} ${MODE_WORDS[mode]}`);
  else if (t.length && mode === "out") plan.push(`no ${join(t.map((k) => TRIGGER_WORDS[k].many), "or")}`);
  if ((a.fasts ?? []).length) plan.push("fast-day foods, no deficit on a fast");

  const todayParts: string[] = [];
  const c = a.cravingTime;
  if (c === "afternoon" || c === "evening")
    todayParts.push(`An ${c} snack with protein and fibre, before cravings`);
  else if (c === "night") todayParts.push("A dinner with protein and fibre, before late cravings");
  else if (c === "stress") todayParts.push("A line on what to reach for first when stress hits");
  if (a.sleep === "<6") todayParts.push("a line on Food about short sleep");
  if (device.after.gentle) todayParts.push("no deficit on this phone");

  return {
    targets,
    plan: plan.length ? cap(plan.join(" · ")) : "Your usual foods, planned to your targets",
    today: todayParts.length ? cap(todayParts.join("; ")) : "Nothing new",
  };
}

/* ---------- what the answers change now ---------- */

/** Hours of the day each craving answer covers: the line shows from a little before until the
 *  end, and not at all once something is logged in the snack slot. */
const CRAVING_HOURS: Record<"afternoon" | "evening" | "night" | "stress", { from: number; to: number }> = {
  afternoon: { from: 12, to: 17 },
  evening: { from: 15, to: 20 },
  night: { from: 19, to: 23 },
  stress: { from: 10, to: 22 },
};

/**
 * Today's one if-then line for cravings (plan §3.4, research §2.5: implementation intentions and
 * protein + fibre before the usual time), or null when there is nothing to say now. The snack is
 * the plan's answer, so once it is logged the line has done its job.
 */
export function cravingLine(S: KayaState, now: Date, snackLogged: boolean): string | null {
  const c = S.profile.eating?.cravingTime;
  if (!c || c === "rarely" || snackLogged) return null;
  const h = now.getHours() + now.getMinutes() / 60;
  const w = CRAVING_HOURS[c];
  if (h < w.from || h >= w.to) return null;
  switch (c) {
    case "afternoon":
      return "If a craving hits this afternoon, eat your snack first — curd, chana or fruit. Protein and fibre take the edge off.";
    case "evening":
      return "If a craving hits this evening, eat your snack first — curd, chana or fruit. Protein and fibre take the edge off.";
    case "night":
      return "If you want something late tonight, have curd or a fruit first — and give dinner some protein and fibre.";
    default:
      return "If stress sends you looking for food, have curd, chana or a fruit first, then decide.";
  }
}

/** The Food hero's sleep line, for someone who said they sleep under six hours (research §2.5,
 *  Al Khatib 2017: short sleep raised intake by ~385 kcal a day, with no change in burn). */
export function sleepLine(S: KayaState): string | null {
  return S.profile.eating?.sleep === "<6"
    ? "Under 6 hours of sleep adds about 385 kcal of appetite a day — sleep helps this budget."
    : null;
}
